import { CircleDot, MoreHorizontal, RefreshCw, Trash2 } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { IconButton, Pill } from '@/design-system';
import { cn } from '@/shared/cn';
import { MENU_CONTENT, MENU_ITEM_BASE } from '@/shared/ui-class';
import type { InstalledPluginSeed } from '@/versions/v1/shared/settings-seed';

function sourceLabel(plugin: InstalledPluginSeed): string {
  return plugin.marketplaceId;
}

type Props = {
  plugin: InstalledPluginSeed;
  bordered: boolean;
  onUninstall: (id: string) => void;
};

export function InstalledPluginCard({ plugin, bordered, onUninstall }: Props) {
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuId = useId();
  const menuRef = useRef<HTMLDivElement>(null);

  const trustRequired = plugin.enabled && !plugin.trusted;
  const dotClass =
    plugin.enabled && plugin.trusted
      ? 'text-emerald-500'
      : trustRequired
        ? 'text-amber-500'
        : 'text-zinc-300 dark:text-zinc-700';

  const canCheckUpdates = Boolean(plugin.marketplaceId);

  useEffect(() => {
    if (!menuOpen) return;

    const onPointerDown = (event: globalThis.MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const openDetail = useCallback(() => {
    navigate(`/settings/plugins/${plugin.id}`);
  }, [navigate, plugin.id]);

  const onKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openDetail();
      }
    },
    [openDetail],
  );

  const stopProp = useCallback((e: ReactMouseEvent<HTMLElement>) => e.stopPropagation(), []);

  const handleRefresh = useCallback(() => {
    if (!plugin.marketplaceId || refreshing) return;
    setRefreshing(true);
    window.setTimeout(() => setRefreshing(false), 800);
  }, [plugin.marketplaceId, refreshing]);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Open plugin: ${plugin.name}`}
      onClick={openDetail}
      onKeyDown={onKeyDown}
      className={cn(
        'flex cursor-pointer items-center gap-4 px-5 py-4 transition-colors hover:bg-[var(--glass-surface-strong)]',
        bordered ? 'border-t border-[var(--glass-border)]' : '',
      )}
    >
      <CircleDot className={`h-3.5 w-3.5 flex-shrink-0 ${dotClass}`} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium">{plugin.name}</span>
          <Pill>v{plugin.version}</Pill>
          {trustRequired ? <Pill tone="danger">Trust required</Pill> : null}
        </div>
        {plugin.description ? (
          <p className="mt-0.5 truncate text-sm text-muted">{plugin.description}</p>
        ) : null}
        <p className="mt-0.5 truncate text-xs text-zinc-400 dark:text-zinc-500">
          {sourceLabel(plugin)}
        </p>
      </div>

      <div className="flex flex-shrink-0 items-center gap-1" onClick={stopProp}>
        {canCheckUpdates ? (
          <IconButton
            label="Check for updates"
            onClick={() => handleRefresh()}
            disabled={refreshing}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          </IconButton>
        ) : null}

        <div ref={menuRef} className="relative inline-flex">
          <IconButton
            label="Plugin actions"
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            aria-controls={menuId}
            onClick={() => setMenuOpen((value) => !value)}
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </IconButton>
          {menuOpen ? (
            <div
              id={menuId}
              role="menu"
              className={cn(MENU_CONTENT, 'absolute right-0 top-full z-50 mt-1 w-[180px]')}
              onClick={stopProp}
            >
              <button
                type="button"
                role="menuitem"
                className={cn(
                  MENU_ITEM_BASE,
                  'w-full text-left text-red-600 dark:text-red-400',
                )}
                onClick={() => {
                  setMenuOpen(false);
                  onUninstall(plugin.id);
                }}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Uninstall
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
